import React, { useEffect, useMemo, useState } from 'react';
import i18n from "i18n";

import currencies from '@shared/lib/data/currencies';
import odd_formats from '@shared/lib/data/odd_formats';
import timezones from '@shared/lib/data/timezones';
import ErrorBag from '@shared/components/base/ErrorBag';
import { platformLogin, platformRegister } from '../../../features/auth/api';

function normalizeErrors(err) {
  const data = err?.response?.data;
  if (data && typeof data === 'object') return data;
  return { message: [err?.message || 'Request failed'] };
}

function RegistrationForm({ onSuccess }) {
  const [errors, setErrors] = useState({});
  const [loader, setLoader] = useState(false);
  const [form_data, setForm_data] = useState({
    firstname: null,
    lastname: null,
    email_address: null,
    password: null,
    pass_confirmation: null,
    odds_format: 'eu',
    currency_id: 'EUR',
    timezone: 'UTC',
    language_id: 'en-en',
  });

  const currenciesList = useMemo(() => currencies || [], []);
  const oddFormatsList = useMemo(() => odd_formats || [], []);
  const timezonesList = useMemo(() => timezones || [], []);

  const handleFormInputChange = (event) => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    setForm_data(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validate = () => {
    const input = form_data;
    const inputErrors = {};
    const regex = /^([a-zA-Z]|[à-ú]|[À-Ú]|[â-û]|[Â-Û]|[ãñõÃÑÕäëïöüÿÄËÏÖÜŸ]|-)+$/;
    let isValid = true;

    if (!input.firstname) {
      isValid = false;
      inputErrors["firstname"] = [i18n.t('validate.firstname.required')];
    }
    if (input.firstname) {
      if (!input.firstname.match(regex)) {
        isValid = false;
        inputErrors["firstname"] = [i18n.t('validate.firstname.alpha')];
      }
    }
    if (!input.lastname) {
      isValid = false;
      inputErrors["lastname"] = [i18n.t('validate.lastname.required')];
    }
    if (!input.email_address) {
      isValid = false;
      inputErrors["email"] = [i18n.t('validate.email.required')];
    }
    if (!input.password) {
      isValid = false;
      inputErrors["password"] = [i18n.t('validate.password.required')];
    }
    if (!input.pass_confirmation) {
      isValid = false;
      inputErrors["pass_confirmation"] = [i18n.t('validate.pass_confirmation.required')];
    }
    if (input.password && input.pass_confirmation) {
      if (input.password !== input.pass_confirmation) {
        isValid = false;
        inputErrors["pass_confirmation"] = [i18n.t('validate.pass_confirmation.mismatch')];
      }
    }

    setErrors(inputErrors);
    return isValid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      return false;
    }

    setLoader(true);
    setErrors({});
    try {
      await platformRegister(form_data);
      const payload = await platformLogin({
        email_address: form_data.email_address,
        password: form_data.password,
        remember_me: true,
      });
      onSuccess?.(payload);
      return true;
    } catch (err) {
      const normalized = normalizeErrors(err);
      const next = normalized?.errors && typeof normalized.errors === 'object' ? normalized.errors : normalized;
      setErrors(next || { message: ["Request failed"] });
      return false;
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const detectedLanguage = (navigator.language || 'en-en').toLowerCase();
    setForm_data(prev => ({
      ...prev,
      timezone: detectedTimezone || prev.timezone,
      language_id: detectedLanguage || prev.language_id,
    }));
  }, []);

  return (
    <div className="w-full">
      <form className="w-full" onSubmit={handleSubmit}>
        <fieldset disabled={loader} className="disabled:opacity-50">

          <div className="w-full mb-4">
            <input
              className="w-full px-4 py-3 text-base text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0b8e36] focus:border-[#0b8e36] transition-colors placeholder:text-gray-500"
              type="text"
              placeholder={i18n.t('firstname')}
              name="firstname"
              required
              value={form_data.firstname || ''}
              onChange={handleFormInputChange}
            />
            <ErrorBag target="firstname" errors={errors} />
          </div>
          <div className="w-full mb-4">
            <input
              className="w-full px-4 py-3 text-base text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0b8e36] focus:border-[#0b8e36] transition-colors placeholder:text-gray-500"
              type="text"
              placeholder={i18n.t('lastname')}
              name="lastname"
              required
              value={form_data.lastname || ''}
              onChange={handleFormInputChange}
            />
            <ErrorBag target="lastname" errors={errors} />
          </div>
          <div className="w-full mb-4">
            <input
              className="w-full px-4 py-3 text-base text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0b8e36] focus:border-[#0b8e36] transition-colors placeholder:text-gray-500"
              type="email"
              placeholder={i18n.t('e_mail')}
              name="email_address"
              required
              value={form_data.email_address || ''}
              onChange={handleFormInputChange}
            />
            <ErrorBag target="email_address" errors={errors} />
          </div>
          <div className="w-full mb-4">
            <input
              className="w-full px-4 py-3 text-base text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0b8e36] focus:border-[#0b8e36] transition-colors placeholder:text-gray-500"
              id="form-login-password-2"
              type="password"
              name="password"
              placeholder={i18n.t('password')}
              value={form_data.password || ''}
              onChange={handleFormInputChange}
              required
            />
            <ErrorBag target="password" errors={errors} />
          </div>

          <div className="w-full mb-4">
            <input
              className="w-full px-4 py-3 text-base text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0b8e36] focus:border-[#0b8e36] transition-colors placeholder:text-gray-500"
              id="form-login-password-3"
              type="password"
              name="pass_confirmation"
              placeholder={i18n.t('pass_confirmation')}
              value={form_data.pass_confirmation || ''}
              onChange={handleFormInputChange}
              required
            />
            <ErrorBag target="pass_confirmation" errors={errors} />
          </div>

          <div className="w-full mb-4">
            <select
              className="w-full px-4 py-3 text-base text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0b8e36] focus:border-[#0b8e36] transition-colors"
              name="odds_format"
              value={form_data.odds_format}
              onChange={handleFormInputChange}
            >
              {oddFormatsList.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="w-full mb-4">
            <select
              className="w-full px-4 py-3 text-base text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0b8e36] focus:border-[#0b8e36] transition-colors"
              name="currency_id"
              value={form_data.currency_id}
              onChange={handleFormInputChange}
            >
              {currenciesList.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="w-full mb-4">
            <select
              className="w-full px-4 py-3 text-base text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0b8e36] focus:border-[#0b8e36] transition-colors"
              name="timezone"
              value={form_data.timezone}
              onChange={handleFormInputChange}
            >
              {timezonesList.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <button
            className="w-full px-6 py-3 bg-[#0b8e36] text-white font-medium rounded-md hover:bg-[#086a28] focus:outline-none focus:ring-2 focus:ring-[#0b8e36] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center"
            type="submit"
            disabled={loader}
          >
            {loader ? i18n.t('loading') : i18n.t('register')}
          </button>
        </fieldset>
      </form>
    </div>
  );
}

export default RegistrationForm;

